import { useMemo, useState } from "react";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
  mfgSolidDangerBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  haystackMatchesCommaSearch,
  parseCommaDelimitedSearchTerms,
} from "@/lib/manufacturing/commaDelimitedSearch";
import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import {
  ComposeMatchDeskComponentList,
  filterComposeComponents,
} from "./ComposeMatchDeskComponentList";
import { LinkStatusBadge, recipeSearchHaystack, workingSkuLabel } from "./composeMatchDeskHelpers";
import type { ComposeMatchDeskProps } from "./composeMatchDeskTypes";

const KINDS: MfgRecipeKind[] = ["assembly", "product", "package", "option", "variant"];

function RefreshIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-3.5 w-3.5" stroke="currentColor">
      <path
        strokeWidth="1.75"
        strokeLinecap="round"
        d="M16.5 10a6.5 6.5 0 1 1-1.9-4.6M16.5 3.5V7h-3.5"
      />
    </svg>
  );
}

export function ManufacturingModelsComposeMatchDesk({
  components,
  products,
  recipes,
  selectedNodeId,
  selectedNode,
  suggestions,
  statusHelp,
  onSelect,
  onMatch,
  onMatchMany,
  onReclassify,
  onSplitByStyle,
  onSplitByStyleMany,
  onRefreshSuggestions,
  onOpenBomSandbox,
  onDuplicateFromNearest,
}: ComposeMatchDeskProps) {
  const [showAlreadyLinked, setShowAlreadyLinked] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [componentQuery, setComponentQuery] = useState("");
  const [hideMatched, setHideMatched] = useState(false);

  const recipesById = useMemo(() => new Map(recipes.map((r) => [r.id, r])), [recipes]);

  const filteredComponents = useMemo(
    () => filterComposeComponents(components, componentQuery, hideMatched),
    [components, componentQuery, hideMatched],
  );

  const usedElsewhereIds = useMemo(() => {
    const ids = new Set<string>();
    for (const n of [...components, ...products]) {
      if (n.id === selectedNodeId) continue;
      if (n.match.status === "matched") ids.add(n.match.recipe_id);
    }
    return ids;
  }, [components, products, selectedNodeId]);

  const linkedRecipe =
    selectedNode?.match.status === "matched"
      ? recipesById.get(selectedNode.match.recipe_id) ?? null
      : null;

  const currentMatchId =
    selectedNode?.match.status === "matched" ? selectedNode.match.recipe_id : null;

  const visibleSuggestions = useMemo(() => {
    if (showAlreadyLinked) return suggestions;
    return suggestions.filter(
      (s) => s.recipe_id === currentMatchId || !usedElsewhereIds.has(s.recipe_id),
    );
  }, [suggestions, showAlreadyLinked, usedElsewhereIds, currentMatchId]);

  const catalogSearchActive = useMemo(
    () => parseCommaDelimitedSearchTerms(catalogQuery).some((t) => t.length >= 2),
    [catalogQuery],
  );

  const catalogHits = useMemo(() => {
    if (!catalogSearchActive || !selectedNode) return [];
    const kindOk = (r: MfgRecipeSummary) => {
      if (selectedNode.role === "product") return r.kind === "product";
      if (selectedNode.kind === "package") {
        return r.kind === "package" || r.kind === "assembly";
      }
      return (
        r.kind === "assembly" ||
        r.kind === "package" ||
        r.kind === "option" ||
        r.kind === "variant"
      );
    };
    const hits: MfgRecipeSummary[] = [];
    for (const r of recipes) {
      if (!kindOk(r)) continue;
      if (!showAlreadyLinked && usedElsewhereIds.has(r.id) && r.id !== currentMatchId) continue;
      if (!haystackMatchesCommaSearch(recipeSearchHaystack(r), catalogQuery)) continue;
      hits.push(r);
      if (hits.length >= 12) break;
    }
    return hits;
  }, [
    catalogQuery,
    catalogSearchActive,
    selectedNode,
    recipes,
    showAlreadyLinked,
    usedElsewhereIds,
    currentMatchId,
  ]);

  const skuLabel = selectedNode ? workingSkuLabel(selectedNode, products) : null;

  function advanceAfterMatch(fromId: string) {
    // Stay inside the filtered part group (e.g. floors), not the global lowest size.
    const next = filteredComponents.find(
      (n) => n.id !== fromId && n.match.status === "unmatched",
    );
    if (next) onSelect(next.id);
  }

  function confirmRecipe(recipeId: string) {
    if (!selectedNode) return;
    const fromId = selectedNode.id;
    onMatch(fromId, recipeId, false);
    setCatalogQuery("");
    advanceAfterMatch(fromId);
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 lg:grid-cols-2">
        <ComposeMatchDeskComponentList
          components={components}
          products={products}
          selectedNodeId={selectedNodeId}
          recipesById={recipesById}
          statusHelp={statusHelp}
          helpOpen={helpOpen}
          onToggleHelp={() => setHelpOpen((v) => !v)}
          componentQuery={componentQuery}
          onComponentQuery={setComponentQuery}
          hideMatched={hideMatched}
          onHideMatched={setHideMatched}
          onSelect={onSelect}
          onBulkMarkTheoretical={
            onMatchMany ? (ids) => onMatchMany(ids, null, true) : undefined
          }
          onBulkSplitStyles={onSplitByStyleMany}
        />

        <div className="space-y-2">
          {selectedNode ? (
            <>
              <div>
                <div className="flex items-center gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Planned slot
                  </div>
                  <LinkStatusBadge status={selectedNode.match.status} />
                </div>
                {skuLabel ? (
                  <div className="mt-0.5 text-xs font-medium text-slate-700 dark:text-slate-200">
                    {skuLabel}
                  </div>
                ) : null}
                <div className="text-sm font-semibold">{selectedNode.code}</div>
                <div className="text-xs text-slate-500">{selectedNode.name}</div>
              </div>

              {linkedRecipe ? (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100">
                  <div className="text-[11px] font-semibold uppercase tracking-wide">
                    Linked to existing recipe
                  </div>
                  <div className="font-semibold">
                    {linkedRecipe.code} · {linkedRecipe.name}
                  </div>
                </div>
              ) : selectedNode.match.status === "theoretical" ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                  <div className="font-semibold">Theoretical — create on commit</div>
                </div>
              ) : null}

              <label className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={showAlreadyLinked}
                  onChange={(e) => setShowAlreadyLinked(e.target.checked)}
                />
                Show recipes already linked in this plan
                {usedElsewhereIds.size > 0 ? (
                  <span className="text-slate-500">({usedElsewhereIds.size} hidden)</span>
                ) : null}
              </label>

              <label className="block text-xs">
                Kind (reclassify before create)
                <select
                  className={`${inputClass} mt-1`}
                  value={selectedNode.kind}
                  onChange={(e) =>
                    onReclassify(selectedNode.id, e.target.value as MfgRecipeKind)
                  }
                >
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto">
                <button
                  type="button"
                  title="Mark theoretical"
                  className={`${mfgSolidDangerBtnClass} !px-2`}
                  onClick={() => onMatch(selectedNode.id, null, true)}
                >
                  Mark
                </button>
                <button
                  type="button"
                  title="Clear link"
                  className={`${ghostBtnClass} !px-2`}
                  onClick={() => onMatch(selectedNode.id, null, false)}
                >
                  Clear
                </button>
                {selectedNode.match.status === "theoretical" && onOpenBomSandbox ? (
                  <button
                    type="button"
                    title="Open BOM sandbox after commit…"
                    className={`${ghostBtnClass} !px-2`}
                    onClick={() => onOpenBomSandbox(selectedNode)}
                  >
                    Open
                  </button>
                ) : null}
                {selectedNode.role === "component" && onDuplicateFromNearest ? (
                  <button
                    type="button"
                    title="Prefer duplicate-from-nearest"
                    className={`${ghostBtnClass} !px-2`}
                    onClick={() => onDuplicateFromNearest(selectedNode)}
                  >
                    Prefer
                  </button>
                ) : null}
                {selectedNode.role === "component" && onSplitByStyle ? (
                  <button
                    type="button"
                    title="Split this shared part into one per style so each can be matched"
                    className={`${ghostBtnClass} !px-2`}
                    onClick={() => onSplitByStyle(selectedNode.id)}
                  >
                    Split styles
                  </button>
                ) : null}
              </div>

              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Catalog suggestions
                </div>
                {onRefreshSuggestions ? (
                  <button
                    type="button"
                    title="Refresh suggestions for this slot"
                    aria-label="Refresh suggestions"
                    className={`${ghostBtnClass} !px-1.5 !py-1`}
                    onClick={onRefreshSuggestions}
                  >
                    <RefreshIcon />
                  </button>
                ) : null}
              </div>
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {visibleSuggestions.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    No unused suggestions. Search the catalog below, enable already-linked, or
                    mark theoretical.
                  </p>
                ) : (
                  visibleSuggestions.map((s) => {
                    const isCurrent = s.recipe_id === currentMatchId;
                    const reused = usedElsewhereIds.has(s.recipe_id) && !isCurrent;
                    return (
                      <button
                        key={`${s.recipe_id}-${s.score}`}
                        type="button"
                        className={`${
                          isCurrent ? mfgPrimaryBtnClass : ghostBtnClass
                        } flex w-full flex-col items-start !py-2 text-left`}
                        onClick={() => confirmRecipe(s.recipe_id)}
                      >
                        <span className="font-semibold">
                          {isCurrent ? "✓ Linked · " : "Use this · "}
                          {s.code}
                          {reused ? " · already in plan" : ""}
                        </span>
                        <span className="text-[11px] opacity-90">{s.name}</span>
                        <span className="text-[10px] opacity-70">
                          Guess: {s.band} ({s.score}) · {s.kind}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="border-t border-slate-200 pt-2 dark:border-zinc-700">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Search catalog
                  <input
                    className={`${inputClass} mt-1 font-normal normal-case tracking-normal`}
                    value={catalogQuery}
                    onChange={(e) => setCatalogQuery(e.target.value)}
                    placeholder="Code or name — commas AND (e.g. floor, 10x12)…"
                  />
                </label>
                {catalogSearchActive ? (
                  <div className="mt-1 max-h-40 space-y-1 overflow-y-auto">
                    {catalogHits.length === 0 ? (
                      <p className="text-xs text-slate-500">No catalog hits.</p>
                    ) : (
                      catalogHits.map((r) => {
                        const isCurrent = r.id === currentMatchId;
                        const reused = usedElsewhereIds.has(r.id) && !isCurrent;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            className={`${
                              isCurrent ? mfgPrimaryBtnClass : ghostBtnClass
                            } flex w-full flex-col items-start !py-2 text-left`}
                            onClick={() => confirmRecipe(r.id)}
                          >
                            <span className="font-semibold">
                              {isCurrent ? "✓ Linked · " : "Use this · "}
                              {r.code}
                              {reused ? " · already in plan" : ""}
                            </span>
                            <span className="text-[11px] opacity-90">
                              {r.name} · {r.kind}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500">
              Select a shared component on the left to link it to a real recipe.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
